<template>
    <div v-if="readerHasData" v-hotkey="shortcutsMaps" class="reader-mode">
        <reader-nav
            :show-button="showPlayerIcon && !showBottomPlayer"
            @show-bottom-player="() => showBottomPlayer = true"
            @close-reader-mode="closeReaderMode"
            @theme-font-styles="(value) => themeFontStyles = value"
        />
        <reader-header @show-bottom-nav="showBottomNav" />
        <reader-themes :media-references="bookInsight.media_references" :is-freemium-bi-locked="isFreemiumBiLocked" :theme-font-styles="themeFontStyles" />
        <transition name="slide-in">
            <reader-nav-bottom v-show="showBottomPlayer" @hide-bottom-player="() => showBottomPlayer = false" />
        </transition>
    </div>
</template>

<script>
import { mapState } from "vuex";
import _sum from "lodash/sum";
import _throttle from "lodash/throttle";
import _isEmpty from "lodash/isEmpty";
import playerEventsMixins from "@/mixins/googleTagManagerMixins";
import freemiumAccessMixins from "@/mixins/freemiumAccessMixins";


export default {
    name: "ReaderMode",
    components: {
        readerNav: () => import(/* webpackChunkName: "reader-header-nav"*/ "./nav"),
        readerNavBottom: () => import(/* webpackChunkName: "reader-bottom-nav"*/ "./bottom-nav"),
        readerHeader: () => import(/* webpackChunkName: "reader-header"*/ "./header"),
        readerThemes: () => import(/* webpackChunkName: "reader-theme"*/ "./theme/index")
    },
    mixins: [playerEventsMixins, freemiumAccessMixins],
    data() {
        return {
            showBottomPlayer: false,
            showPlayerIcon: false,
            themeFontStyles: {
                color: "$medium-gray",
                fontSize: "18px",
                lineHeight: "30px"
            }
        }
    },
    computed: {
        ...mapState({
            readerThemes: state => state.NonfictionReader.readerThemes,
            bookInsight: state => state.NonfictionReader.bookInsight,
            originalAudiobook: state => state.NonfictionReader.originalAudiobook,
            showReaderMode: state => state.NonfictionReader.showReaderMode
        }),
        readerHasData() {
            return !_isEmpty(this.bookInsight);
        },
        shortcutsMaps() {
            return {
                "esc" : this.closeReaderMode,
                "p" : this.playAndPause,
                "f": this.forward,
                "r": this.rewind
            }
        },
        isFreemiumBiLocked() {
            return this.$_freemiumBiLocked();
        }
    },
    watch: {
        "$route": {
            handler(currentRoute, oldRoute) {
                if (currentRoute.name == "book-insight" && !_isEmpty(oldRoute) && oldRoute.name == "book-insight-reader" && this.showReaderMode) {
                    this.closeReaderMode();
                }
            },
            immediate: true
        },
        showReaderMode(newVal) {
            if (newVal) {
                this.runWootric();
            }
        }
    },
    mounted() {
        window.addEventListener("scroll", _throttle(this.setNavThemesPercentage, 700));
    },
    created() {
        scrollTo(0, 0);
    },
    beforeDestroy() {
        window.removeEventListener("scroll", this.setNavThemesPercentage);
    },
    methods: {
        runWootric() {
            this.$_gtmTrackEvent({ event:"run-wootric", action:"init-nps" },);
        },
        setNavThemesPercentage() {
            if (!this.showReaderMode) {
                return;
            }
            const currentScroll = (document.body.scrollTop || document.documentElement.scrollTop);

            if ((document.getElementById("theme-block-container").offsetTop - (currentScroll + document.documentElement.clientHeight)) > 0) {
                return;
            }

            const documentHeight = document.getElementById("theme-block-container").scrollHeight;
            const windowScrollPercentage = (currentScroll / documentHeight) * 100;

            const themeHeights = [];

            const readerThemesWithAudio = this.readerThemes.filter(theme => theme.audio_url);
            const readerThemesLength = this.$_freemiumBiLocked() ? readerThemesWithAudio.length : this.readerThemes.length;
            for (let index = 0; index < readerThemesLength; index++) {
                const themeElement = document.querySelector(`#theme-block-${index + 1}`);
                themeHeights[index] = (themeElement.offsetHeight) / documentHeight * 100;
            }

            const currentScrollPercentages = [];

            for (let index = 0; index < this.readerThemes.length; index++) {
                const currentSum = _sum(themeHeights.slice(0, index + 1));
                const scrollTurn = windowScrollPercentage <= currentSum ? windowScrollPercentage - _sum(themeHeights.slice(0, index)) : themeHeights[index];
                currentScrollPercentages[index] = currentSum - windowScrollPercentage <= themeHeights[index] ? Math.ceil(scrollTurn / themeHeights[index] * 100) : 0;
            }

            this.$store.dispatch("NonfictionReader/setReaderThemeProgress", currentScrollPercentages);
        },
        closeReaderMode() {
            this.$store.dispatch("NonfictionReader/setShowReaderMode", false);
            if (this.$route.name !== "book-insight") {
                this.$router.replace({ name: "book-insight", params: { ...this.$route.params } });
            }
            this.$store.dispatch("NonfictionPlayer/cleanUpData");
            this.$store.dispatch("NonfictionReader/resetProgress");

        },
        showBottomNav(show = false) {
            this.showBottomPlayer = show;
            this.showPlayerIcon = show;
        },
        playAndPause() {
            this.$store.dispatch("NonfictionPlayer/toggle");
        },
        forward() {
            this.$store.dispatch("NonfictionPlayer/setForward");
        },
        rewind() {
            this.$store.dispatch("NonfictionPlayer/setRewind");
        }
    }

}
</script>

<style lang="scss">
.reader-mode {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    background-color: #020202;
    z-index: 20;

}

@media print {
    @page  {
        margin-top: 50px;
        margin-bottom: 50px;
    }

    .reader-mode {
        .reader-header {
            padding: 40px 0 !important;
        }

        .reader-nav, .reader-player, .reader-nav-bottom {
            display: none;
        }
    }
}
</style>
